import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { DismissButton, useOverlay } from '@react-aria/overlays';
import { FocusScope } from '@react-aria/focus';
import { isElementWidthInViewport } from '../../../../lib/utils';

export const Wrapper = styled.div`
    background: var(--color-white);
    box-shadow: var(--shadow-1);
    left: ${(props) => props.left}px;
    position: absolute;
    ${(props) =>
        !props.bottom &&
        css`
            top: 100%;
        `};
    white-space: nowrap;
    width: auto;
    z-index: 2;
    bottom: ${(props) => props.bottom && `${props.bottom}px`};
`;

const Popover = (props) => {
    const ref = useRef(null);
    const { popoverRef = ref, isOpen, onClose, children, pickerRef } = props;

    // Handle events that should cause the popup to close,
    // e.g. blur, clicking outside, or pressing the escape key.
    const { overlayProps } = useOverlay(
        {
            isOpen,
            onClose,
            shouldCloseOnBlur: true,
            isDismissable: true,
        },
        popoverRef,
    );

    const [popoverLeftPosition, setPopoverLeftPosition] = useState(0);
    const [popoverBottomPosition, setPopoverBottomPosition] = useState(null);

    useEffect(() => {
        let bounding = popoverRef?.current?.getBoundingClientRect();

        if (isOpen) {
            if (!(bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight))) {
                setPopoverBottomPosition(pickerRef?.current?.clientHeight);
            }
            if (popoverRef.current && !isElementWidthInViewport(popoverRef.current)) {
                setPopoverLeftPosition(pickerRef?.current?.clientWidth - popoverRef?.current?.clientWidth);
            } else {
                setPopoverLeftPosition(0);
            }
        }
    }, [isOpen, popoverRef, pickerRef]);

    // Sovrascriviamo il comportamento di chiusura al click esterno
    useEffect(() => {
        const onPointerDown = (e) => {
            // Se il click avviene fuori dal popover e fuori dal pulsante che l'ha aperto
            if (
                isOpen &&
                popoverRef.current &&
                !popoverRef.current.contains(e.target) &&
                pickerRef.current &&
                !pickerRef.current.contains(e.target)
            ) {
                onClose(); // Chiudiamo la tendina manualmente
                // NON facciamo e.preventDefault() o e.stopPropagation()
                // così il click prosegue verso l'altro componente
            }
        };

        if (isOpen) {
            // Usiamo capture: true per intercettare il click prima che React Aria lo blocchi
            window.addEventListener('pointerdown', onPointerDown, true);
        }

        return () => {
            window.removeEventListener('pointerdown', onPointerDown, true);
        };
    }, [isOpen, onClose, popoverRef, pickerRef]);

    // Add a hidden <DismissButton> component at the end of the popover
    // to allow screen reader users to dismiss the popup easily.
    return (
        <FocusScope restoreFocus={true}>
            <Wrapper {...overlayProps} ref={popoverRef} left={popoverLeftPosition} bottom={popoverBottomPosition}>
                {children}
                <DismissButton onDismiss={onClose} />
            </Wrapper>
        </FocusScope>
    );
};

Popover.propTypes = {
    children: PropTypes.node,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    popoverRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),
    pickerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),
};

export default Popover;
